import { supabase } from '@/lib/supabase'

export type CenterAdminRole = 'owner' | 'admin'
export type AdminMode = 'platform' | 'center' | 'none'

export interface CenterAdminMembership {
  centerId: string
  centerName: string
  centerLegalName: string
  role: CenterAdminRole
}

export interface AdminScope {
  platformAdmin: boolean
  centerAdminMemberships: CenterAdminMembership[]
  canOpenAdmin: boolean
}

export interface CenterAdminSummary {
  id: string
  name: string
  legalName: string
  country: string
  timezone: string
  memberCount: number
  activeUsers7d: number
  activeUsers30d: number
  reports7d: number
  reports30d: number
  aiCalls7d: number
  aiCalls30d: number
  aiCost30d: number
  aiErrorRate7d: number
  lastActivityAt: string | null
}

export interface CenterAdminSummaryResponse {
  center: CenterAdminSummary
  access: 'platform' | 'center'
  windowDays: number
  generatedAt: string
}

export interface PlatformCentersResponse {
  centers: CenterAdminSummary[]
  windowDays: number
  generatedAt: string
}

type Fetcher = typeof fetch

export function deriveAdminMode(scope: AdminScope | null): AdminMode {
  if (!scope?.canOpenAdmin) return 'none'
  if (scope.platformAdmin) return 'platform'
  return scope.centerAdminMemberships.length > 0 ? 'center' : 'none'
}

export function getAdminModeSidebarLabel(mode: AdminMode): string | null {
  if (mode === 'platform') return 'Superadmin'
  if (mode === 'center') return 'Admin del centro'
  return null
}

export async function obtenerAdminScope(fetcher: Fetcher = fetch): Promise<AdminScope> {
  const response = await fetcher('/api/admin/my-scope', {
    method: 'GET',
    headers: await headersAuth(),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'No se pudo cargar el alcance de administración' }))
    throw new Error(error.message ?? `Error ${response.status}`)
  }

  return response.json() as Promise<AdminScope>
}

export async function obtenerCenterAdminSummary(
  centerId: string,
  fetcher: Fetcher = fetch,
): Promise<CenterAdminSummaryResponse> {
  const response = await fetcher(`/api/admin/center-summary?centerId=${encodeURIComponent(centerId)}`, {
    method: 'GET',
    headers: await headersAuth(),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'No se pudo cargar el resumen del centro' }))
    throw new Error(error.message ?? `Error ${response.status}`)
  }

  return response.json() as Promise<CenterAdminSummaryResponse>
}

export async function obtenerPlatformCenters(fetcher: Fetcher = fetch): Promise<PlatformCentersResponse> {
  const response = await fetcher('/api/admin/centers', {
    method: 'GET',
    headers: await headersAuth(),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'No se pudo cargar el resumen de centros' }))
    throw new Error(error.message ?? `Error ${response.status}`)
  }

  return response.json() as Promise<PlatformCentersResponse>
}

async function headersAuth(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (data.session?.access_token) headers.Authorization = `Bearer ${data.session.access_token}`
  return headers
}
